import { createAction } from 'redux-act'

import { ApiError } from 'types'

import {
  Parcel,
  NormalizedDeliveryModes,
  Address,
  Item,
  GiftCard,
  SavedCard,
  CartState,
  CustomerState,
  DeliveryState,
  AddressSuggestionsState,
  PaymentState,
  PickupPlace,
  NormalizedPrepare,
  AdyenPaymentDataForNewCard,
  AdyenPaymentDataForSavedCard,
  CybersourcePaymentDataForNewCard,
  CybersourcePaymentDataForSavedCard,
  PhoneNumber,
  FetchedCityOrDistrictType,
  NormalizedCheckoutSettings,
  NormalizedContext,
  TimeSlot,
  NormalizedPickupPlace,
  RupayData,
  PaymentMode,
  CreditGuardData,
} from '../types'

export const setShouldShowErrorScreen = createAction<boolean>('Set should show error screen')

export const resetToInitialState = createAction('Reset checkout state')
export const postProceedStart = createAction('Post proceed start')
export const postProceedSuccess = createAction('Post proceed success')
export const postProceedFail = createAction('Post proceed fail')
export const postProceedReset = createAction('Post proceed reset')

// SERIALIZED_ACTION to be able to subscribe using hm.eventBus
export const ABTestContextSuccess = createAction('AB_TEST_CONTEXT_SUCCESS')

export const fetchContextStart = createAction('Fetch context start')
export const fetchContextSuccess = createAction<NormalizedContext>('fetch context success')
export const fetchContextFail =
  createAction<{ shouldShowErrorScreen?: boolean }>('Fetch context fail')

export const postPrepareStart = createAction('Post prepare start')

export const postPrepareSuccess = createAction<NormalizedPrepare>('Post prepare success')

export const postPrepareFail = createAction('Post prepare fail')
export const updateCustomerDetailsReset = createAction('Reset customer details status')
export const updateCustomerDetailsStart = createAction('Update customer details start')
export const updateCustomerDetailsSuccess = createAction<{
  cart: CartState
  customer: CustomerState
}>('Update customer details success')
export const updateCustomerDetailsFail = createAction<ApiError[]>('Update customer details fail')

export const updateCustomerVatNumber = createAction<string>('Update Customer Vat Number')
export const updateVatInvoiceType = createAction<string>('Update customer invoice type')

export const updateCart = createAction<{ cart: CartState }>('Update cart data')
export const updateDelivery = createAction<{ delivery: DeliveryState }>('Update delivery data')

export const fetchPickupPointsStart = createAction('fetch pickup points start')
export const fetchPickupPointsSuccess = createAction<PickupPlace[]>('fetch pickup points success')
export const fetchPickupPointsFail = createAction('fetch pickup points fail')

export const fetchCnCPointsStart = createAction('fetch CnC points start')
export const fetchCnCPointsSuccess = createAction<PickupPlace[]>('fetch CnC points success')
export const fetchCnCPointsFail = createAction('fetch CnC points fail')

export const postNewAddressReset = createAction('Post new address reset')
export const postNewAddressStart = createAction('Post new address start')
export const postNewAddressSuccess = createAction<NormalizedContext>('Post new address success')
export const postNewAddressFail = createAction<ApiError[]>('Post new address fail')

export const updateAddressStart = createAction('Update address start')
export const updateAddressSuccess = createAction<NormalizedContext>('Update address success')
export const updateAddressFail = createAction<ApiError[]>('Update address fail')

export const setShouldShowProductsDetails = createAction<boolean>(
  'Set should show products details',
)
export const setParcelToShowInProductDetails = createAction<string>(
  'Set parcel to show in product details',
)

export const setHasUndeliverableItems = createAction<boolean>(
  'Sets whether user has undeliverable items',
)

export const setAddressSuggestions =
  createAction<AddressSuggestionsState>('Set address suggestions')
export const removeAddressSuggestions = createAction('Remove address suggestions')
export const showAddressSuggestions = createAction('Show address suggestions')
export const hideAddressSuggestions = createAction('Hide address suggestions')
export const setUserEditedAddressFromSuggestions = createAction(
  'Set user has clicked edit button in address suggestions',
)

export const setAvailableDeliverySpeeds = createAction<string[]>('Set available delivery speeds')

export const continueWithoutHazmat = createAction('Continue without Hazmat items')

export const setSelectedDeliveryMethod = createAction<string>('set selected delivery method')
export const setSelectedDeliveryAddress = createAction<Address>('set selected delivery address')
export const updateAddressDeliveryModes = createAction('update address delivery modes')
export const setSelectedDeliverySpeed = createAction<string | null>('set selected delivery Speed')
export const setSelectedPickupPoint = createAction<string>('set selected pickup point')
export const setSelectedCnCPoint = createAction<string>('set selected CnC point')
export const setSelectedPickupPlacePerParcel = createAction<{
  parcelId: string | undefined
  selectedPickupPlace: NormalizedPickupPlace | undefined
}>('set selected pickupPlace for a parcel')
export const setDeliveryPhoneNumber = createAction<PhoneNumber>('set delivery phone number')

// TODO: MERGED_DELIVERY_SECTION_CO_EXPERIMENT , temporary workaround
// COWEB-1690
export const setSelectedHomeDeliveryAddressId = createAction<string | undefined>(
  'set selected home address',
)
export const resetPickupPointsState = createAction('reset pickup points state')
export const resetCnCPointsState = createAction('reset CnC points state')

export const selectDeliverySpeedStart = createAction('select delivery speed start')
export const selectDeliverySpeedSuccess = createAction('select delivery speed success')
export const selectDeliverySpeedFail = createAction<ApiError[]>('select delivery speed fail')

export const setError = createAction<ApiError>('set error')
export const clearErrorByCode = createAction<string>('clear error by code')
export const clearErrorByTarget = createAction<string>('clear error by target')

export const setOutOfStockItems = createAction<Item[] | undefined>('set out of stock items')
export const clearOutOfStockItems = createAction('clear out of stock items')
export const savePaymentDetails = createAction<
  | AdyenPaymentDataForNewCard
  | AdyenPaymentDataForSavedCard
  | CybersourcePaymentDataForNewCard
  | CybersourcePaymentDataForSavedCard
>('save payment details')
export const patchPaymentStart = createAction('patch payment start')
export const patchPaymentSuccess =
  createAction<{ newPaymentState: PaymentState }>('patch payment success')
export const patchPaymentFail = createAction('patch payment fail')
export const setSelectedPaymentMethod = createAction<PaymentMode>('set selected payment method')

export const setLastUsedPickupPlacePostalCode = createAction<string>(
  'change last used PUP postal code',
)

export const resetGiftCardModalData = createAction('Reset gift card modal data')

export const addGiftCardStart = createAction('Add gift cards start')
export const addGiftCardSuccess = createAction('Add gift cards success')
export const addGiftCardFail = createAction('Add gift cards fail')

export const removeGiftCardStart = createAction('Remove gift cards start')
export const removeGiftCardSuccess = createAction('Remove gift cards success')
export const removeGiftCardFail = createAction<GiftCard[]>('Remove gift cards fail')

export const fetchCheckoutPrepareSuccess = createAction('Fetch checkout prepare success')

export const removeCreditCardStart = createAction('Remove credit card start')
export const removeCreditCardSuccess = createAction('Remove credit card success')
export const removeCreditCardFail = createAction<SavedCard[]>('Remove credit card fail')

export const updateRupayData = createAction<RupayData>('Updates rupay data')
export const resetRupayData = createAction('Reset rupay data')

export const updateCreditGuardData = createAction<CreditGuardData>('Update Credit Guard data')
export const resetCreditGuardData = createAction('Reset Credit Guard data')

export const setAvailablePaymentMethods = createAction<PaymentMode[]>(
  'Updates the available payment methods',
)
// -------------
// Parcels
//--------------

export const setSplitOrderModalUI = createAction<boolean>('Set split order modal state')
export const setMainDeliveryModalUI = createAction<boolean>('Set main delivery modal state')

interface SetCrossbrandsParams {
  parcel?: Parcel
  isOpen: boolean
}

export const setCrossbrandsDeliveryModalUI = createAction<SetCrossbrandsParams>(
  'Set crossbrands delivery modal state',
)
export const setDropShipDeliveryModalUI = createAction<boolean>(
  'Set drop ship parcel delivery modal state',
)

export const setBOPISInformationModalUI = createAction<boolean>('set BOPIS information modal state')
export const setBOPISSplitOrderModalUI = createAction<boolean>('set BOPIS split order modal')

export const updateDeliveryModesStart = createAction('update delivery modes start')
export const updateDeliveryModesSuccess = createAction<NormalizedDeliveryModes>(
  'update delivery modes success',
)

export const updateDeliveryModesFail = createAction('update delivery modes fail')

export const editItemQuantityStart = createAction<string>('Start editing item quantity')
export const editItemQuantitySuccess = createAction<string>('Edit item quantity success')
export const editItemQuantityFail = createAction<string>('Edit item quantity fail')

// -------
// Load Cities & Districts
// -------

export const updateFetchedCities = createAction<FetchedCityOrDistrictType>('Update cities')
export const updateFetchedDistricts = createAction<FetchedCityOrDistrictType>('Update districts')

// -------
// Load PostalCode
// -------
export const updateFetchedPostalCode = createAction<string>('Update postalCode')

// -------
// Fetch Checkout Settings
// -------

export const fetchSettingsSuccess = createAction<NormalizedCheckoutSettings>(
  'Fetch checkout settings success',
)

// -------
// Preselection of pickup place
// -------
export const setPreselectedPickupPlace = createAction<string>('Update the preselected pickup place')

// -------
// TimeSlot selection
// -------
export const setSelectedTimeSlot = createAction<TimeSlot>('Update the selected timeslot')

// Used in experiment - remove on cleanup [COPAYNOL-2027]
export const setHasCompletePurchaseClicked = createAction<boolean>(
  'Sets whether the user click Complete purchase CTA',
)

export const setHasOpenedDeliverySection = createAction(
  'Sets whether the user has opened the delivery section',
)
